import { hyp } from '../core/exactMath';
import type { GridPoint } from '../core/types';
import type { WorldMap } from '../world/WorldMap';
import { BuildingStore, type Building } from './buildings/BuildingStore';
import { ECONOMY, POPULATION, STOCK_RESOURCES, buildingDef, isBuildingTypeId, type BuildingTypeId, type ResourceAmounts, type StockResource } from './buildings/definitions';
import { originForCursor } from './buildings/footprint';
import type { PlacementResult } from './buildings/placement';
import type { Command, CommandResult } from './commands';
import { Colony, type ColonySnapshot } from './colony/Colony';
import { Economy, type EconomySnapshot } from './economy/Economy';
import { UnitStore, type TargetRef, type TrainingJob, type Unit } from './units/UnitStore';
import { MAX_UNITS_PER_COMMAND, STANCES, TRAIN_QUEUE_LIMIT, isUnitTypeId, unitDef, type UnitTypeId } from './units/unitDefs';
import type { Projectile } from './combat/Projectiles';
import { InfectedDirector, SPECIALS, type InfectedConfig, type InfectedSnapshot } from './infected/InfectedDirector';
import { Defence } from './defence/Defence';
import { MatchRules } from './modes/MatchRules';
import { MODES, type ModeConfig } from './modes/modes';
import { RAID_ARMY, scoreRaid, type BaseLayout, type RaidScore } from './raid/bases';
import { hostile } from './combat/rules';
import type { OwnerId } from './ids';
import { BEACON_COOLDOWN, REPAIR } from './buildings/definitions';
import { isGated } from './content/pass';
import { ColonyAI } from './ai/ColonyAI';
import { ResearchStore, isResearchId, researchDef } from './research/Research';
import { Objectives, type ObjectivesConfig } from './objectives/Objectives';
import { footprintTiles } from './buildings/footprint';
import { isPlayerId, type PlayerId } from './ids';
import { nearestOpenTiles } from './pathing/AStar';
import type { MatchPlayer, MatchSetup } from './match';

export interface SimulationConfig {
  /** Ownership rule: players build within this many tiles of their own buildings. */
  readonly buildRadius: number;
  /** Each player's starting resources (defaults to ECONOMY.startingResources). */
  readonly startingResources?: ResourceAmounts;
  /** Survivors each player starts with (defaults to POPULATION.startingSurvivors; capped by housing). */
  readonly startingSurvivors?: number;
  /** Test / sandbox only: Rangers given to the first player at the start (next to their Command Center). */
  readonly startingUnits?: number | Readonly<Partial<Record<UnitTypeId, number>>>;
  /**
   * Test / sandbox only (Phase 6): a mixed squad owned by the SECOND player,
   * placed this many tiles from the first player's Command Center, so combat
   * can be tried before zombies (Phase 7) and multiplayer exist.
   */
  readonly startingEnemies?: number;
  /** Phase 9: the game mode (start, infected schedule, victory rule). Default: Sandbox rules, but no infected unless `infected` or a mode asks. */
  readonly mode?: ModeConfig;
  /** The infected (Phase 7): starting walkers, hordes, wander seed. Omitted = the mode's (none without a mode). `{}` = HORDE.initial walkers, hordes on. */
  readonly infected?: InfectedConfig;
  /** Phase 13 raid: the defender's (second player's) saved base; the first player gets the raiding army. */
  readonly raid?: { readonly base: BaseLayout };
  /**
   * Season-pass content each player may use (D112). Omitted = everything is
   * open (headless tests, sandbox). Online, the SERVER fills this from its own
   * records; offline, the local profile does.
   */
  readonly unlocks?: Readonly<Record<string, readonly string[]>>;
  /** Campaign objectives: nests to destroy, camps to reach (D124). */
  readonly objectives?: ObjectivesConfig;
}

/** Serialisable state summary (grows each phase; used for tests, saves and sync later). */
export interface SimulationSnapshot {
  readonly tick: number;
  readonly players: readonly MatchPlayer[];
  readonly nextBuildingId: number;
  readonly buildings: readonly Building[];
  readonly economy: EconomySnapshot;
  readonly colony: ColonySnapshot;
  readonly units: { units: Unit[]; queues: Record<string, TrainingJob[]>; nextUnitId: number; projectiles: Projectile[] };
  readonly infected: InfectedSnapshot;
  readonly defence: { cooldowns: Record<string, number>; powered: number[] };
  readonly match: ReturnType<MatchRules['snapshot']>;
  readonly beacons: Record<string, number>;
  readonly ai: ReturnType<ColonyAI['snapshot']>;
  readonly objectives: ReturnType<Objectives['snapshot']> | null;
  readonly research: ReturnType<ResearchStore['snapshot']>;
}

/**
 * The game simulation: world + players + buildings, advanced in fixed ticks
 * and changed only through validated commands. Engine-free: no Phaser, no
 * DOM, no wall-clock time, no Math.random — so the same code can run on the
 * authoritative server later (MULTIPLAYER_ARCHITECTURE.md).
 */
export class Simulation {
  readonly buildings: BuildingStore;
  readonly economy: Economy;
  readonly colony: Colony;
  readonly units: UnitStore;
  readonly infected: InfectedDirector;
  readonly defence: Defence;
  readonly match: MatchRules;
  readonly mode: ModeConfig;
  /** Raid bookkeeping (Phase 13): defender buildings at the start, destroyed ones, the lootable stockpile. */
  readonly raid: { readonly total: number; readonly destroyed: Set<number>; readonly stored: BaseLayout['stored']; readonly name: string } | null = null;
  /** Player → team (allies never fight). */
  readonly teams: ReadonlyMap<OwnerId, number>;
  /** Repair already paid for but not yet done, per player, in scrap (1 scrap buys 1 / scrapPerHealth health). */
  private readonly repairCredit = new Map<PlayerId, number>();
  private readonly players = new Map<PlayerId, MatchPlayer>();
  private tickCount = 0;

  constructor(
    readonly world: WorldMap,
    readonly setup: MatchSetup,
    readonly config: SimulationConfig,
  ) {
    this.buildings = new BuildingStore(world, { buildRadius: config.buildRadius });
    for (const p of setup.players) {
      if (this.players.has(p.id)) throw new Error(`Duplicate player ${p.id}`);
      if (!world.playerSlots[p.slot]) throw new Error(`Map has no slot ${p.slot} for ${p.id}`);
      this.players.set(p.id, p);
    }
    this.applyStartingState();
    const defender = setup.players[1];
    if (config.raid && defender) this.raid = this.placeRaidBase(defender.id, config.raid.base);
    // Created after the starting structures exist, so storage and housing already include them.
    const ids = setup.players.map((p) => p.id);
    this.mode = config.mode ?? MODES.sandbox;
    this.unlocks = config.unlocks ?? null;
    this.teams = new Map(setup.players.map((p) => [p.id as OwnerId, p.team]));
    this.colony = new Colony(this.buildings, ids, { ...POPULATION, startingSurvivors: config.startingSurvivors ?? config.mode?.startingSurvivors ?? POPULATION.startingSurvivors });
    const colony = this.colony;
    this.economy = new Economy(this.buildings, ids, config.startingResources ?? (config.mode?.startingResources ? { ...ECONOMY.startingResources, ...config.mode.startingResources } : ECONOMY.startingResources), {
      workRatio: (id) => colony.workRatio(id),
      foodNeeded: (p) => colony.foodNeeded(p),
      onShortfall: (p) => colony.starve(p),
    });
    this.units = new UnitStore(world, this.buildings);
    this.units.teams = this.teams;
    const first = setup.players[0];
    if (first && config.startingUnits) {
      const cc = this.buildings.ownedBy(first.id).find((b) => b.type === 'command_center');
      const mix: Partial<Record<UnitTypeId, number>> = typeof config.startingUnits === 'number' ? { ranger: config.startingUnits } : config.startingUnits;
      for (const [type, n] of Object.entries(mix) as [UnitTypeId, number][]) {
        if (unitDef(type).infected) continue;
        for (let i = 0; i < n && cc; i++) this.units.spawn(type, first.id, cc);
      }
    }
    if (first && setup.players[1] && config.startingEnemies) this.placeEnemySquad(first.id, setup.players[1].id, config.startingEnemies);
    // No infected unless the match asks for them (the game does; most headless tests do not).
    this.infected = new InfectedDirector(world, this.units, this.buildings, ids, config.infected ?? (config.mode ? config.mode.infected : { initial: 0, waves: false }));
    this.defence = new Defence(this.buildings, this.units, ids);
    this.objectives = config.objectives && setup.players[0] ? new Objectives(config.objectives, world, this.buildings, this.units, this.infected, this.colony, this.economy, setup.players[0].id) : null;
    const objectives = this.objectives;
    this.match = new MatchRules(this.mode, setup.players, this.buildings, this.units, this.infected, {
      get nestsLeft() {
        return objectives?.nestsLeft ?? 0;
      },
      get rescued() {
        return objectives?.rescued ?? 0;
      },
    });
    // D126: an overrun home or workplace infects the people inside — they rise as walkers where it stood.
    this.buildings.subscribe((e) => {
      if (e.type !== 'removed' || !e.destroyed || !isPlayerId(e.building.ownerId)) return;
      const def = buildingDef(e.building.type);
      const people = def.workerSlots ?? (def.housing ? Math.ceil(def.housing / 3) : 0);
      if (!people) return;
      const c = { x: e.building.origin.x + Math.floor(def.footprint.width / 2), y: e.building.origin.y + Math.floor(def.footprint.height / 2) };
      if (!this.units.enemiesNear(e.building.ownerId, c.x, c.y, 4).some((u) => unitDef(u.type).infected)) return;
      // Applied after the units have moved this tick (never in the middle of someone's attack).
      this.overruns.push({ at: c, owner: e.building.ownerId, people });
    });
    // D128: research bonuses reach every system through one lookup.
    this.research = new ResearchStore();
    const bonus = this.research.bonus.bind(this.research);
    this.units.bonus = bonus;
    this.defence.bonus = bonus;
    this.economy.bonus = (o, k) => bonus(o, k);
    this.infected.biteBonus = (o) => bonus(o, 'bite');
    this.research.subscribe((e) => e.type === 'done' && e.research === 'efficient_generators' && this.defence.markDirty());
    const bots = setup.players.filter((p) => p.control === 'ai').map((p) => ({ id: p.id, difficulty: p.difficulty ?? 'normal' }));
    this.ai = bots.length ? new ColonyAI(this, bots) : null;
    if (this.raid && defender && first) {
      const raid = this.raid;
      const ledger = this.economy.ledger(defender.id);
      for (const r of STOCK_RESOURCES) ledger.take(r, ledger.get(r));
      for (const r of STOCK_RESOURCES) if (raid.stored[r]) ledger.add(r, raid.stored[r]!);
      const cc = this.buildings.ownedBy(defender.id).find((b) => b.type === 'command_center')!;
      const base = config.raid!.base;
      for (const g of base.garrison) {
        const at = nearestOpenTilesFor(this, { x: cc.origin.x + g.dx, y: cc.origin.y + g.dy }, 1)[0];
        if (at) this.units.add(g.type, defender.id, at);
      }
      const home = this.buildings.ownedBy(first.id).find((b) => b.type === 'command_center');
      for (const [type, n] of Object.entries(RAID_ARMY) as [UnitTypeId, number][]) for (let i = 0; i < n && home; i++) this.units.spawn(type, first.id, home);
      this.buildings.subscribe((e) => {
        if (e.type === 'removed' && e.destroyed && e.building.ownerId === defender.id) raid.destroyed.add(e.building.id);
      });
    }
    this.units.repairPayer = (owner, _buildingId, health) => (isPlayerId(owner) && this.players.has(owner) ? this.payRepair(owner, health) : 0);
  }

  get tick(): number {
    return this.tickCount;
  }

  player(id: PlayerId): MatchPlayer | null {
    return this.players.get(id) ?? null;
  }

  allPlayers(): MatchPlayer[] {
    return [...this.players.values()];
  }

  /** Placement check for previews: the same rules `apply` enforces, including affordability. */
  validatePlacement(playerId: PlayerId, type: BuildingTypeId, origin: GridPoint): PlacementResult {
    const r = this.unitsCheck(this.buildings.validate(type, origin, playerId));
    if (!r.ok || !this.players.has(playerId)) return r;
    const short = this.missingFor(playerId, type);
    return short ? { ...r, ok: false, failures: [...r.failures, 'insufficient_resources'], message: short } : r;
  }

  /** Buildings cannot be placed on top of units. */
  private unitsCheck(r: PlacementResult): PlacementResult {
    if (!this.units) return r;
    const inWay = this.units.unitsOnTiles(footprintTiles(r.origin, r.definition.footprint));
    if (!inWay.length) return r;
    const blocked = new Set(inWay.map((u) => `${Math.round(u.x)},${Math.round(u.y)}`));
    return {
      ...r,
      ok: false,
      failures: [...r.failures, 'units_in_way'],
      tiles: r.tiles.map((t) => (blocked.has(`${t.x},${t.y}`) ? { ...t, ok: false, reason: t.reason ?? 'units_in_way' } : t)),
      message: r.ok ? 'Units are standing there — move them first.' : r.message,
    };
  }

  /** "Need 12 more wood." when the player cannot pay for `type`, else null. */
  private missingFor(playerId: PlayerId, type: BuildingTypeId): string | null {
    return this.missingCost(playerId, buildingDef(type).cost);
  }

  private missingCost(playerId: PlayerId, cost: ResourceAmounts): string | null {
    const a = this.economy.ledger(playerId).canAfford(cost);
    if (a.ok) return null;
    const parts = STOCK_RESOURCES.filter((r) => (a.missing[r] ?? 0) > 0).map((r) => `${a.missing[r]} more ${r}`);
    return `Not enough resources — need ${parts.join(' and ')}.`;
  }

  /** What demolishing this building would return (rounded down; capped later by free storage). */
  refundFor(buildingId: number): ResourceAmounts {
    const b = this.buildings.get(buildingId);
    if (!b) return {};
    const share = b.state === 'complete' ? ECONOMY.refundComplete : ECONOMY.refundUnderConstruction;
    const cost = buildingDef(b.type).cost;
    const out: Partial<Record<StockResource, number>> = {};
    for (const r of STOCK_RESOURCES) {
      const v = Math.floor((cost[r] ?? 0) * share);
      if (v > 0) out[r] = v;
    }
    return out;
  }

  private readonly unlocks: Readonly<Record<string, readonly string[]>> | null;
  /** Computer-controlled colonies (D115), or null. */
  readonly ai: ColonyAI | null;
  /** Campaign objectives (D124), or null. */
  readonly objectives: Objectives | null;
  /** D128 tech tree. */
  readonly research: ResearchStore;
  /** D126: buildings overrun this tick, turned into walkers after the units step. */
  private readonly overruns: { at: GridPoint; owner: PlayerId; people: number }[] = [];
  /** Horde Beacon id → tick when it may be used again (D114). */
  private readonly beaconReady = new Map<number, number>();

  /** May `player` use this unit / building? Only season-pass content is ever locked. */
  isUnlocked(player: PlayerId, content: string): boolean {
    if (!this.unlocks || !isGated(content)) return true;
    return this.unlocks[player]?.includes(content) ?? false;
  }

  /** Seconds until this Horde Beacon can be used again (0 = ready). */
  beaconCooldown(buildingId: number): number {
    return Math.max(0, ((this.beaconReady.get(buildingId) ?? 0) - this.tickCount) / 10);
  }

  /** Validate without changing anything. */
  validate(cmd: Command): CommandResult {
    if (!isPlayerId(cmd.playerId) || !this.players.has(cmd.playerId)) return reject('unknown_player', 'Unknown player.');
    if (this.match.result) return reject('match_over', 'The match is over.');
    if (this.match.statusOf(cmd.playerId) === 'defeated') return reject('defeated', 'Your colony has fallen.');
    switch (cmd.type) {
      case 'placeBuilding': {
        if (!isBuildingTypeId(cmd.building) || !isGridPoint(cmd.origin)) return reject('invalid_payload', 'Malformed command.');
        const def = buildingDef(cmd.building);
        if (!def.playerBuildable) return reject('not_player_buildable', `${def.name} cannot be built by players.`);
        if (!this.isUnlocked(cmd.playerId, cmd.building)) return reject('locked', `${def.name} is locked — unlock it in the Season Pass.`);
        const r = this.unitsCheck(this.buildings.validate(cmd.building, cmd.origin, cmd.playerId));
        if (!r.ok) return reject(r.failures[0]!, r.message);
        const short = this.missingFor(cmd.playerId, cmd.building);
        if (short) return reject('insufficient_resources', short);
        return { ok: true, buildingId: this.buildings.nextBuildingId };
      }
      case 'setWorkers': {
        if (!Number.isInteger(cmd.buildingId)) return reject('invalid_payload', 'Malformed command.');
        const c = this.colony.checkSetWorkers(cmd.playerId, cmd.buildingId, cmd.workers);
        return c.ok ? { ok: true, buildingId: cmd.buildingId } : reject(c.code, c.message);
      }
      case 'demolishBuilding': {
        const b = Number.isInteger(cmd.buildingId) ? this.buildings.get(cmd.buildingId) : null;
        if (!b) return reject('no_such_building', 'That building no longer exists.');
        if (b.ownerId !== cmd.playerId) return reject('not_owner', 'You can only demolish your own buildings.');
        const def = buildingDef(b.type);
        if (!def.demolishable) return reject('not_demolishable', `The ${def.name} cannot be demolished.`);
        return { ok: true, buildingId: b.id };
      }
      case 'trainUnit': {
        const b = Number.isInteger(cmd.buildingId) ? this.buildings.get(cmd.buildingId) : null;
        if (!b) return reject('no_such_building', 'That building no longer exists.');
        if (b.ownerId !== cmd.playerId) return reject('not_owner', 'You can only train at your own buildings.');
        if (!isUnitTypeId(cmd.unit)) return reject('invalid_payload', 'Malformed command.');
        const def = buildingDef(b.type);
        const ud = unitDef(cmd.unit);
        if (!def.trains?.includes(cmd.unit)) return reject('cannot_train', `The ${def.name} cannot train ${ud.name}s.`);
        if (!this.isUnlocked(cmd.playerId, cmd.unit)) return reject('locked', `${ud.name}s are locked — unlock them in the Season Pass.`);
        if (b.state !== 'complete') return reject('not_ready', `The ${def.name} is still under construction.`);
        if (this.units.queue(b.id).length >= TRAIN_QUEUE_LIMIT) return reject('queue_full', `The queue is full (${TRAIN_QUEUE_LIMIT}).`);
        const short = this.missingCost(cmd.playerId, ud.cost);
        if (short) return reject('insufficient_resources', short);
        if (!this.colony.canRecruit(cmd.playerId, ud.survivors)) return reject('not_enough_survivors', 'No survivors left to recruit.');
        return { ok: true, buildingId: b.id };
      }
      case 'cancelTraining': {
        const b = Number.isInteger(cmd.buildingId) ? this.buildings.get(cmd.buildingId) : null;
        if (!b) return reject('no_such_building', 'That building no longer exists.');
        if (b.ownerId !== cmd.playerId) return reject('not_owner', 'You can only cancel your own training.');
        if (!this.units.queue(b.id).length) return reject('nothing_queued', 'Nothing is being trained.');
        return { ok: true, buildingId: b.id };
      }
      case 'surrender':
        return { ok: true, buildingId: 0 };
      case 'upgradeBuilding': {
        const b = Number.isInteger(cmd.buildingId) ? this.buildings.get(cmd.buildingId) : null;
        if (!b) return reject('no_such_building', 'That building no longer exists.');
        if (b.ownerId !== cmd.playerId) return reject('not_owner', 'You can only upgrade your own buildings.');
        const def = buildingDef(b.type);
        if (!def.upgrade) return reject('not_upgradable', `The ${def.name} has no upgrade.`);
        if (b.level >= 2) return reject('already_upgraded', `Already upgraded to ${def.upgrade.name}.`);
        if (b.state !== 'complete') return reject('not_ready', `The ${def.name} is still under construction.`);
        if (b.upgradeProgress !== null) return reject('busy', 'The upgrade is already under way.');
        const short = this.missingCost(cmd.playerId, def.upgrade.cost);
        if (short) return reject('insufficient_resources', short);
        return { ok: true, buildingId: b.id };
      }
      case 'repairBuilding': {
        const bad = this.checkUnitIds(cmd.playerId, cmd.unitIds);
        if (bad) return bad;
        const b = Number.isInteger(cmd.buildingId) ? this.buildings.get(cmd.buildingId) : null;
        if (!b) return reject('no_such_building', 'That building no longer exists.');
        if (b.ownerId !== cmd.playerId) return reject('not_owner', 'You can only repair your own buildings.');
        if (b.state !== 'complete') return reject('not_ready', 'Finish building it first.');
        if (b.health >= b.maxHealth) return reject('not_damaged', 'It is not damaged.');
        const repairers = cmd.unitIds.filter((id) => !!unitDef(this.units.get(id)!.type).repairRate);
        if (!repairers.length) return reject('cannot_repair', 'Only Engineers can repair. Train them at a Workshop.');
        if (this.economy.ledger(cmd.playerId).get('scrap') <= 0) return reject('insufficient_resources', 'Repairs need scrap.');
        return { ok: true, buildingId: b.id, unitIds: repairers };
      }
      case 'research': {
        if (!isResearchId(cmd.research)) return reject('invalid_payload', 'Malformed command.');
        const def = researchDef(cmd.research);
        if (!this.buildings.ownedBy(cmd.playerId).some((b) => b.type === 'research_lab' && b.state === 'complete')) return reject('not_ready', 'Build a Research Lab first.');
        if (this.research.has(cmd.playerId, cmd.research)) return reject('already_researched', `${def.name} is already researched.`);
        if (def.requires && !this.research.has(cmd.playerId, def.requires)) return reject('requires', `Research ${researchDef(def.requires).name} first.`);
        if (this.research.active(cmd.playerId)) return reject('busy', 'The lab is busy with another research.');
        const short = this.missingCost(cmd.playerId, def.cost);
        if (short) return reject('insufficient_resources', short);
        return { ok: true, buildingId: 0 };
      }
      case 'lureHorde': {
        const b = Number.isInteger(cmd.buildingId) ? this.buildings.get(cmd.buildingId) : null;
        if (!b || b.type !== 'horde_beacon') return reject('no_such_building', 'That Horde Beacon no longer exists.');
        if (b.ownerId !== cmd.playerId) return reject('not_owner', 'You can only use your own Horde Beacon.');
        if (b.state !== 'complete') return reject('not_ready', 'The Horde Beacon is still under construction.');
        if (this.infected.secondsToWave === null) return reject('no_hordes', 'There are no hordes in this match.');
        const wait = this.beaconCooldown(b.id);
        if (wait > 0) return reject('cooldown', `The beacon is recharging (${Math.ceil(wait)} s).`);
        if (!isPlayerId(cmd.target) || !this.players.has(cmd.target) || cmd.target === cmd.playerId) return reject('no_such_target', 'Pick a rival colony.');
        if (!hostile(cmd.playerId, cmd.target, this.teams)) return reject('not_enemy', 'That colony is your ally.');
        if (this.match.statusOf(cmd.target) !== 'playing') return reject('no_such_target', 'That colony has already fallen.');
        return { ok: true, buildingId: b.id };
      }
      case 'moveUnits':
      case 'attackMove':
      case 'attack':
      case 'setStance':
      case 'stopUnits': {
        const bad = this.checkUnitIds(cmd.playerId, cmd.unitIds);
        if (bad) return bad;
        if (cmd.type === 'setStance') {
          if (!STANCES.includes(cmd.stance)) return reject('invalid_payload', 'Malformed command.');
        } else if (cmd.type === 'moveUnits' || cmd.type === 'attackMove') {
          if (!isGridPoint(cmd.target) || !this.world.inBounds(cmd.target.x, cmd.target.y)) return reject('out_of_bounds', 'That is off the map.');
        } else if (cmd.type === 'attack') {
          const t = cmd.target as TargetRef | undefined;
          if (!t || (t.kind !== 'unit' && t.kind !== 'building') || !Number.isInteger(t.id)) return reject('invalid_payload', 'Malformed command.');
          const owner = t.kind === 'unit' ? this.units.get(t.id)?.ownerId : this.buildings.get(t.id)?.ownerId;
          if (owner === undefined) return reject('no_such_target', 'That target no longer exists.');
          if (!hostile(cmd.playerId, owner, this.teams)) return reject('not_enemy', 'You can only attack enemies.');
        }
        return { ok: true, buildingId: 0, unitIds: [...cmd.unitIds] };
      }
      default:
        return reject('invalid_payload', 'Unknown command.');
    }
  }

  private checkUnitIds(playerId: PlayerId, ids: unknown): CommandResult | null {
    if (!Array.isArray(ids) || ids.length === 0 || ids.length > MAX_UNITS_PER_COMMAND || !ids.every((i) => Number.isInteger(i))) {
      return reject('invalid_payload', 'Malformed command.');
    }
    if (new Set(ids).size !== ids.length) return reject('invalid_payload', 'Malformed command.');
    for (const id of ids as number[]) {
      const u = this.units.get(id);
      if (!u) return reject('no_such_unit', 'That unit no longer exists.');
      if (u.ownerId !== playerId) return reject('not_owner', 'You can only command your own units.');
    }
    return null;
  }

  private refundJob(playerId: PlayerId, unit: UnitTypeId): void {
    const ud = unitDef(unit);
    const ledger = this.economy.ledger(playerId);
    for (const r of STOCK_RESOURCES) if (ud.cost[r]) ledger.add(r, ud.cost[r]!);
    this.economy.markChanged(playerId);
    this.colony.returnSurvivors(playerId, ud.survivors);
  }

  /** Queue contents of a building (for the HUD). */
  trainingQueue(buildingId: number): readonly TrainingJob[] {
    return this.units.queue(buildingId);
  }

  /** Validate, then apply. Invalid commands change nothing. */
  apply(cmd: Command): CommandResult {
    const v = this.validate(cmd);
    if (!v.ok) return v;
    switch (cmd.type) {
      case 'placeBuilding': {
        // Validated above, so the spend cannot fail and the footprint is free.
        this.economy.ledger(cmd.playerId).spend(buildingDef(cmd.building).cost);
        this.economy.markChanged(cmd.playerId);
        const id = this.buildings.add(cmd.building, cmd.origin, cmd.playerId).id;
        this.economy.flush();
        this.colony.flush();
        return { ok: true, buildingId: id };
      }
      case 'demolishBuilding': {
        const refund = this.refundFor(cmd.buildingId);
        // Units still queued there are cancelled with a full refund (survivors return if there is room).
        for (let job = this.units.cancelLast(cmd.buildingId); job; job = this.units.cancelLast(cmd.buildingId)) this.refundJob(cmd.playerId, job.unit);
        // Remove first: if storage shrinks, the refund is capped by the new capacity.
        this.buildings.remove(cmd.buildingId);
        const ledger = this.economy.ledger(cmd.playerId);
        for (const r of STOCK_RESOURCES) if (refund[r]) ledger.add(r, refund[r]!);
        this.economy.markChanged(cmd.playerId);
        this.economy.flush();
        this.colony.flush();
        return { ok: true, buildingId: cmd.buildingId };
      }
      case 'setWorkers':
        this.colony.setWorkers(cmd.playerId, cmd.buildingId, cmd.workers);
        this.colony.flush();
        return { ok: true, buildingId: cmd.buildingId };
      case 'trainUnit': {
        const ud = unitDef(cmd.unit);
        this.economy.ledger(cmd.playerId).spend(ud.cost);
        this.economy.markChanged(cmd.playerId);
        this.colony.recruit(cmd.playerId, ud.survivors);
        this.units.enqueue(cmd.buildingId, cmd.unit);
        this.economy.flush();
        this.colony.flush();
        return { ok: true, buildingId: cmd.buildingId };
      }
      case 'cancelTraining': {
        const job = this.units.cancelLast(cmd.buildingId)!;
        this.refundJob(cmd.playerId, job.unit);
        this.economy.flush();
        this.colony.flush();
        return { ok: true, buildingId: cmd.buildingId };
      }
      case 'moveUnits':
        this.units.move(cmd.unitIds, cmd.target);
        return v;
      case 'attack':
        this.units.attack(cmd.unitIds, cmd.target);
        return v;
      case 'stopUnits':
        this.units.stop(cmd.unitIds);
        return v;
      case 'attackMove':
        this.units.attackMove(cmd.unitIds, cmd.target);
        return v;
      case 'setStance':
        this.units.setStance(cmd.unitIds, cmd.stance);
        return v;
      case 'surrender':
        this.match.forfeit(cmd.playerId, 'Surrendered');
        return v;
      case 'upgradeBuilding': {
        const b = this.buildings.get(cmd.buildingId)!;
        this.economy.ledger(cmd.playerId).spend(buildingDef(b.type).upgrade!.cost);
        this.economy.markChanged(cmd.playerId);
        this.buildings.startUpgrade(b.id);
        this.economy.flush();
        return v;
      }
      case 'repairBuilding':
        this.units.repair(v.ok && v.unitIds ? v.unitIds : [], cmd.buildingId);
        return v;
      case 'research':
        this.economy.ledger(cmd.playerId).spend(researchDef(cmd.research).cost);
        this.economy.markChanged(cmd.playerId);
        this.research.start(cmd.playerId, cmd.research);
        this.economy.flush();
        return v;
      case 'lureHorde':
        this.infected.setLure(cmd.target, cmd.playerId);
        this.beaconReady.set(cmd.buildingId, this.tickCount + BEACON_COOLDOWN * 10);
        return v;
    }
  }

  /** Advance one fixed tick of `dt` seconds. */
  step(dt: number): void {
    this.tickCount++;
    this.buildings.tick(dt);
    this.infected.step(dt);
    this.defence.step(dt);
    this.units.step(dt, (buildingId, unit) => {
      const b = this.buildings.get(buildingId);
      if (b && isPlayerId(b.ownerId)) this.units.spawn(unit, b.ownerId, b);
    });
    for (const o of this.overruns.splice(0, this.overruns.length)) {
      const n = Math.min(SPECIALS.overrunMax, o.people, this.colony.population(o.owner));
      if (n <= 0) continue;
      this.colony.recruit(o.owner, n);
      this.infected.overrun(o.at, n, o.owner);
    }
    this.economy.step(dt);
    this.colony.step(dt, (p) => this.canGrow(p));
    this.objectives?.step(dt);
    this.research.step(dt, (p) => this.buildings.ownedBy(p).some((b) => b.type === 'research_lab' && b.state === 'complete'));
    this.economy.flush();
    this.colony.flush();
    this.match.step(dt);
    this.ai?.step(this.tickCount);
    // Raid: a wiped-out raiding party ends the raid (the attacker cannot train).
    const attacker = this.setup.players[0];
    if (this.raid && attacker && !this.match.result && this.tickCount > 10 && this.units.ownedBy(attacker.id).length === 0) this.match.forfeit(attacker.id, 'Raiding party wiped out');
  }

  /** Raid result so far (Phase 13), or null outside raids. */
  raidScore(): RaidScore | null {
    const r = this.raid;
    if (!r) return null;
    const defender = this.setup.players[1]!;
    const cc = !this.buildings.ownedBy(defender.id).some((b) => b.type === 'command_center');
    return scoreRaid(r.total, r.destroyed.size, cc, r.stored);
  }

  /** Lay out a saved base around a player's Command Center (complete buildings at their levels; tiles that do not fit are skipped). */
  private placeRaidBase(p: PlayerId, base: BaseLayout): { total: number; destroyed: Set<number>; stored: BaseLayout['stored']; name: string } {
    const cc = this.buildings.ownedBy(p).find((b) => b.type === 'command_center')!;
    let placed = 1;
    for (const b of base.buildings) {
      const origin = { x: cc.origin.x + b.dx, y: cc.origin.y + b.dy };
      if (!this.buildings.validate(b.type, origin).ok) continue;
      const id = this.buildings.add(b.type, origin, p, { complete: true, preplaced: true }).id;
      if (b.level === 2) this.buildings.setupLevel(id, 2);
      placed++;
    }
    return { total: placed, destroyed: new Set(), stored: base.stored, name: base.name };
  }

  /** Short deterministic fingerprint of the whole state (FNV-1a of the snapshot) — for replay and desync checks. */
  stateHash(): string {
    // `control` says which device drives a seat (local / remote) — it differs per client and is not game state.
    const s = this.snapshot();
    return fnv1a(JSON.stringify({ ...s, players: s.players.map(({ control: _c, ...p }) => p) }));
  }

  /**
   * Sandbox squad for trying combat: Rangers and Heavies (2:1) owned by `enemy`,
   * on open tiles 12 tiles from `near`'s Command Center toward the map centre
   * (out of sight of the base, so nothing fights until someone moves).
   */
  private placeEnemySquad(near: PlayerId, enemy: PlayerId, count: number): void {
    const cc = this.buildings.ownedBy(near).find((b) => b.type === 'command_center');
    if (!cc) return;
    const fp = buildingDef(cc.type).footprint;
    const c = { x: cc.origin.x + (fp.width - 1) / 2, y: cc.origin.y + (fp.height - 1) / 2 };
    const mid = { x: (this.world.width - 1) / 2, y: (this.world.height - 1) / 2 };
    const len = hyp(mid.x - c.x, mid.y - c.y) || 1;
    const at = { x: Math.round(c.x + ((mid.x - c.x) / len) * 12), y: Math.round(c.y + ((mid.y - c.y) / len) * 12) };
    const spots = nearestOpenTilesFor(this, at, count);
    spots.forEach((s, i) => this.units.add(i % 3 === 2 ? 'heavy' : 'ranger', enemy, s));
  }

  /** Pay for `health` points of repair, scrap up front (1 scrap = the next 10 health). Returns points paid for. */
  private payRepair(p: PlayerId, health: number): number {
    const ledger = this.economy.ledger(p);
    let paid = 0;
    let credit = this.repairCredit.get(p) ?? 0;
    for (let i = 0; i < health; i++) {
      if (credit < REPAIR.scrapPerHealth - 1e-9) {
        if (ledger.get('scrap') < 1) break;
        ledger.take('scrap', 1);
        this.economy.markChanged(p);
        credit += 1;
      }
      credit -= REPAIR.scrapPerHealth;
      paid++;
    }
    this.repairCredit.set(p, credit);
    if (paid) this.economy.flush();
    return paid;
  }

  /** Food conditions for newcomers: enough food in store and no shortage. */
  canGrow(p: PlayerId): boolean {
    return this.economy.ledger(p).get('food') >= POPULATION.growthMinFood && !this.economy.hasShortage(p);
  }

  snapshot(): SimulationSnapshot {
    return {
      tick: this.tickCount,
      players: this.allPlayers(),
      nextBuildingId: this.buildings.nextBuildingId,
      buildings: this.buildings.all(),
      economy: this.economy.snapshot(),
      colony: this.colony.snapshot(),
      units: this.units.snapshot(),
      infected: this.infected.snapshot(),
      defence: this.defence.snapshot(),
      match: this.match.snapshot(),
      ai: this.ai?.snapshot() ?? [],
      objectives: this.objectives?.snapshot() ?? null,
      research: this.research.snapshot(),
      beacons: Object.fromEntries([...this.beaconReady].sort((a, b) => a[0] - b[0]).map(([k, t]) => [String(k), t])),
    };
  }

  /**
   * Explicit starting state: each player's starting structures, completed,
   * centred on their map slot. Placed through the normal placement rules
   * (minus territory), so a map that cannot fit them fails loudly.
   */
  private applyStartingState(): void {
    for (const p of this.setup.players) {
      const slot = this.world.playerSlots[p.slot]!;
      for (const s of this.setup.startingBuildings) {
        const def = buildingDef(s.type);
        const origin = originForCursor(slot, def.footprint);
        const r = this.buildings.validate(s.type, origin);
        if (!r.ok) throw new Error(`Starting ${def.name} for ${p.id} cannot be placed at ${origin.x},${origin.y}: ${r.message}`);
        this.buildings.add(s.type, origin, p.id, { complete: true, preplaced: true });
      }
    }
  }
}

export function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

function nearestOpenTilesFor(sim: Simulation, at: GridPoint, count: number): GridPoint[] {
  return nearestOpenTiles(sim.world.width, sim.world.height, sim.units.passable, at, count);
}

function reject(code: Extract<CommandResult, { ok: false }>['code'], message: string): CommandResult {
  return { ok: false, code, message };
}

function isGridPoint(p: unknown): p is GridPoint {
  return typeof p === 'object' && p !== null && Number.isInteger((p as GridPoint).x) && Number.isInteger((p as GridPoint).y);
}

/** Local command sink: applies commands directly to an in-process Simulation. */
export class LocalCommandSink {
  constructor(private readonly sim: Simulation) {}

  submit(cmd: Command): CommandResult {
    return this.sim.apply(cmd);
  }
}
